import styled from '@emotion/styled';
import { Typography } from '@material-ui/core';
import { ClassAttributes, createElement } from 'react';
import { FormattedMessage } from 'react-intl';

import { ProjectType } from '../../../graphql/generated/graphql';
import theme from '../../../theme';
import { SPACING_REGULAR, SPACING_UNIT } from '../../constant/spacing';
import { gridLayoutContainer, gridLayoutItem } from '../../styleUtility/gridLayout';
import AspectRatioFixed from '../primitive/AspectRatioFixed';
import LfToBr from '../primitive/LfToBr';
import VideoScreen from '../primitive/VideoScreen';

export interface Props {
  projectId: string;
  projectType: Pick<ProjectType, 'thumbnailImageUrl' | 'sampleVideoUrl' | 'title' | 'description'>;
  hasProjectChange: boolean;
  noShowDiff?: boolean;
  className?: string;
}

const ProjectTypeSummary = ({
  projectId,
  projectType,
  className,
  noShowDiff,
  hasProjectChange,
}: ClassAttributes<HTMLElement> & Props) => {
  const thumbnailImage = projectType.thumbnailImageUrl || '/assets/empty_thumbnail.png';

  const sampleScreen = projectType.sampleVideoUrl ? (
    <AspectRatioFixed aspectRatio={[4, 3]}>
      <VideoScreen src={projectType.sampleVideoUrl} controls nodownload />
    </AspectRatioFixed>
  ) : (
    <ThumbnailImage src={thumbnailImage} />
  );

  return (
    <Root className={className}>
      <Heading variant="h6">
        <FormattedMessage id="project_type.heading" />
        {!noShowDiff && hasProjectChange ? (
          <DiffTitle href={`/projects/${projectId}/diff`}>
            <FormattedMessage id="project_type.heading_diff" />
          </DiffTitle>
        ) : null}
      </Heading>

      <SampleScreen>{sampleScreen}</SampleScreen>

      <Title variant="h5">{projectType.title}</Title>

      <Description variant="body1">
        <LfToBr text={projectType.description} />
      </Description>
    </Root>
  );
};

export const Root = styled('div')(
  gridLayoutContainer({
    columns: ['1fr', `${SPACING_UNIT * 3}px`, '1fr'],
    rows: ['auto', 'auto', '1fr'],
    areas: [
      ['heading', 'heading', 'heading'],
      ['sample', '.', 'title'],
      ['sample', '.', 'description'],
    ],
  }),
  {
    marginBottom: `${SPACING_UNIT * 3}px`,
  },
);

const Heading = styled(Typography)(gridLayoutItem('heading'), {
  marginBottom: SPACING_UNIT * 3,
  display: 'flex',
  justifyContent: 'space-between',
});

const SampleScreen = styled('div')(gridLayoutItem('sample', { justifySelf: 'center' }), { width: '100%' });

const ThumbnailImage = styled('img')({ maxWidth: '100%' });

const Title = styled(Typography)(gridLayoutItem('title'), { marginBottom: SPACING_REGULAR });

const Description = styled(Typography)(gridLayoutItem('description'));

const DiffTitle = styled('a')({
  wordWrap: 'break-word',
  wordBreak: 'break-word' as any,
  marginBottom: SPACING_UNIT,
  fontSize: 16,
  fontWeight: 500,
  lineHeight: '24px',
  fontFamily: "'Hiragino Sans', 'Roboto', 'Helvetica', 'Arial', 'sans-serif'",
  color: theme.palette.primary.main,
  textDecoration: 'none',
  cursor: 'pointer',
  '&:hover': {
    color: theme.palette.primary.main,
    textDecoration: 'underline',
  },
  '&:active': {
    color: theme.palette.primary.main,
  },
  '&:visited': {
    color: theme.palette.primary.main,
  },
});
export default ProjectTypeSummary;
